import fs from 'node:fs'
import path from 'node:path'
import * as p from '@clack/prompts'
import type { CreateCommandOptions, CreateSchemaKind } from '@core-engine/commands/runtime.js'
import {
  type AuthoredEntityOrSingleSchema,
  type LoadedAuthoredGeneratedSchema,
  validateAuthoredGeneratedSchema,
  writeAuthoredGeneratedSchema
} from '@core-engine/schema/add-field.js'
import type {
  FormColumn,
  FormField,
  FormSchema,
  SchemaField,
  SchemaTab
} from '@core-engine/schema/types.js'
import { snapshotRootExists } from '@core-engine/snapshots/store.js'
import { stringifyProjectJson } from '@core-engine/utils/json-format.js'
import { resolveProjectPaths } from '@next-adapter/config/paths.js'
import { resolveConfig } from '@next-adapter/config.js'
import { runGenerateCommand } from './generate.js'
import {
  cancel,
  deriveAvailableFieldName,
  displayLabel,
  fail,
  formColumnTypeForField,
  isInteractiveSession,
  type PromptFieldOptions,
  promptConfirm,
  promptField,
  promptSelectValue,
  promptText,
  type SchemaPromptContext
} from './schema-prompts.js'

const CREATE_PROMPT_CONTEXT: SchemaPromptContext = {
  cancelMessage: 'Create schema cancelled.'
}
const SCHEMA_NAME_PATTERN = /^[a-z][a-zA-Z0-9-]*$/
const SCHEMA_NAME_VALIDATION_MESSAGE =
  'Use letters, numbers, and hyphens, starting with a lowercase letter.'

type EntitySingleLayout = 'fields' | 'slot' | 'tabs'
type CreateSchemaAction = 'write' | 'addMore' | 'cancel'
const ADD_FIELD_CONFIRM_INITIAL_VALUE = true
const CREATE_FIELD_PROMPT_OPTIONS: PromptFieldOptions = {
  deriveName: true,
  derivePlaceholder: true,
  promptAdvancedOptions: false,
  promptHeight: false,
  promptHint: false,
  typeFirst: true
}

interface SchemaMetadata {
  name: string
  label: string
  description: string
  icon: string
  titleLabel: string
}

function normalizeKind(kind: string): CreateSchemaKind {
  if (kind === 'entity' || kind === 'single' || kind === 'form') {
    return kind
  }

  fail(`Unsupported schema kind "${kind}". Use "entity", "single", or "form".`)
}

function validateSchemaName(name: string): string | undefined {
  if (!name.trim()) {
    return 'Enter a schema name.'
  }

  if (!SCHEMA_NAME_PATTERN.test(name)) {
    return SCHEMA_NAME_VALIDATION_MESSAGE
  }

  return undefined
}

function humanizeName(name: string): string {
  return name
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

function validateProvidedSchemaName(schemaName: string): void {
  const error = validateSchemaName(schemaName)
  if (error) {
    fail(`Invalid schema name "${schemaName}". ${error}`)
  }
}

function deriveSchemaNameFromLabel(label: string): string {
  const base = label
    .trim()
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/['"]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-+/g, '-')
    .toLowerCase()

  return /^[a-z]/.test(base) ? base : base ? `schema-${base}` : 'schema'
}

function assertSchemaDoesNotExist(schemasDir: string, name: string): void {
  const rootPath = path.join(schemasDir, `${name}.json`)
  const formPath = path.join(schemasDir, 'forms', `${name}.json`)
  const existing = [rootPath, formPath].filter((filePath) => fs.existsSync(filePath))

  if (existing.length > 0) {
    fail(
      `Schema "${name}" already exists. Remove or rename the existing schema first:\n${existing.map((filePath) => `  - ${filePath}`).join('\n')}`
    )
  }
}

async function promptMetadata(
  kind: CreateSchemaKind,
  providedName: string | undefined,
  schemasDir: string
): Promise<SchemaMetadata> {
  if (providedName) {
    validateProvidedSchemaName(providedName)
    assertSchemaDoesNotExist(schemasDir, providedName)
  }

  const defaultLabel = providedName ? humanizeName(providedName) : undefined
  const label = await promptText(CREATE_PROMPT_CONTEXT, 'Schema label', defaultLabel)
  const name = providedName ?? deriveSchemaNameFromLabel(label)
  if (!providedName) {
    const error = validateSchemaName(name)
    if (error) {
      fail(`Schema label "${label}" derived invalid schema name "${name}". ${error}`)
    }
    assertSchemaDoesNotExist(schemasDir, name)
  }
  const titleLabel = await promptText(CREATE_PROMPT_CONTEXT, 'First field label', {
    placeholder: 'Name'
  })

  return {
    name,
    label,
    description: kind === 'form' ? `${label} form` : `${label} schema`,
    icon: kind === 'form' ? 'Mail' : kind === 'single' ? 'Settings' : 'FileText',
    titleLabel
  }
}

function createSchemaTitleField(label: string): SchemaField {
  return {
    name: 'title',
    type: 'string',
    label,
    required: true
  }
}

function createFormTitleField(label: string): FormField {
  return {
    name: 'title',
    type: 'text',
    label,
    required: true
  }
}

function getFieldNames(fields: Array<{ name?: string }>): string[] {
  return fields
    .map((field) => field.name)
    .filter((name): name is string => typeof name === 'string' && name.length > 0)
}

function createFieldPromptOptions(fields: Array<{ name?: string }>): PromptFieldOptions {
  return {
    ...CREATE_FIELD_PROMPT_OPTIONS,
    existingNames: getFieldNames(fields)
  }
}

async function promptAdditionalSchemaFields(
  kind: 'entity' | 'single',
  schemasDir: string,
  fields: SchemaField[]
): Promise<void> {
  while (
    await promptConfirm(
      CREATE_PROMPT_CONTEXT,
      'Add another field?',
      ADD_FIELD_CONFIRM_INITIAL_VALUE
    )
  ) {
    fields.push(
      (await promptField(
        CREATE_PROMPT_CONTEXT,
        kind,
        schemasDir,
        'schema',
        0,
        createFieldPromptOptions(fields)
      )) as SchemaField
    )
  }
}

async function promptAdditionalFormFields(
  schemasDir: string,
  fields: FormField[],
  message = 'Add another field?'
): Promise<void> {
  while (await promptConfirm(CREATE_PROMPT_CONTEXT, message, ADD_FIELD_CONFIRM_INITIAL_VALUE)) {
    await promptFormField(schemasDir, fields)
  }
}

async function promptFormField(schemasDir: string, fields: FormField[]): Promise<void> {
  fields.push(
    (await promptField(
      CREATE_PROMPT_CONTEXT,
      'form',
      schemasDir,
      'schema',
      0,
      createFieldPromptOptions(fields)
    )) as FormField
  )
}

async function promptSchemaSlotFields(
  kind: 'entity' | 'single',
  schemasDir: string,
  titleField: SchemaField
): Promise<AuthoredEntityOrSingleSchema['slot']> {
  const mainFields: SchemaField[] = [titleField]
  const sidebarFields: SchemaField[] = []

  while (
    await promptConfirm(
      CREATE_PROMPT_CONTEXT,
      'Add another field?',
      ADD_FIELD_CONFIRM_INITIAL_VALUE
    )
  ) {
    await promptSchemaSlotField(kind, schemasDir, mainFields, sidebarFields)
  }

  return {
    main: { fields: mainFields },
    ...(sidebarFields.length > 0 ? { sidebar: { fields: sidebarFields } } : {})
  }
}

async function promptSchemaSlotField(
  kind: 'entity' | 'single',
  schemasDir: string,
  mainFields: SchemaField[],
  sidebarFields: SchemaField[]
): Promise<void> {
  const slot = await promptSelectValue(
    CREATE_PROMPT_CONTEXT,
    'Field slot',
    [
      { value: 'main', label: 'main' },
      { value: 'sidebar', label: 'sidebar' }
    ],
    'main'
  )
  const field = (await promptField(
    CREATE_PROMPT_CONTEXT,
    kind,
    schemasDir,
    'schema',
    0,
    createFieldPromptOptions([...mainFields, ...sidebarFields])
  )) as SchemaField
  if (slot === 'sidebar') {
    sidebarFields.push(field)
  } else {
    mainFields.push(field)
  }
}

async function promptTabSlotFields(
  kind: 'entity' | 'single',
  schemasDir: string,
  titleField: SchemaField | undefined
): Promise<{ main: SchemaField[]; sidebar: SchemaField[] }> {
  const main: SchemaField[] = titleField ? [titleField] : []
  const sidebar: SchemaField[] = []

  while (true) {
    const hasFields = main.length + sidebar.length > 0
    const addField = await promptConfirm(
      CREATE_PROMPT_CONTEXT,
      hasFields ? 'Add another tab child field?' : 'Add a tab child field?',
      !hasFields
    )

    if (!addField) {
      if (!hasFields) {
        p.log.warn('Tabs need at least one child field.')
        continue
      }
      return { main, sidebar }
    }

    const slot = await promptSelectValue(
      CREATE_PROMPT_CONTEXT,
      'Child field slot',
      [
        { value: 'main', label: 'main' },
        { value: 'sidebar', label: 'sidebar' }
      ],
      'main'
    )
    const child = (await promptField(
      CREATE_PROMPT_CONTEXT,
      kind,
      schemasDir,
      'schema',
      1,
      createFieldPromptOptions([...main, ...sidebar])
    )) as SchemaField
    if (slot === 'sidebar') {
      sidebar.push(child)
    } else {
      main.push(child)
    }
  }
}

async function promptSchemaTab(
  kind: 'entity' | 'single',
  schemasDir: string,
  titleField: SchemaField | undefined,
  existingTabs: SchemaTab[]
): Promise<SchemaTab> {
  const label = await promptText(
    CREATE_PROMPT_CONTEXT,
    'Tab label',
    titleField ? 'Content' : undefined
  )
  const name = deriveAvailableFieldName(label, getFieldNames(existingTabs))
  const layout = await promptSelectValue(CREATE_PROMPT_CONTEXT, 'Tab layout', [
    { value: 'fields', label: 'Single fields array' },
    { value: 'slot', label: 'Main/sidebar slots' }
  ] as const)

  if (layout === 'fields') {
    const fields: SchemaField[] = titleField ? [titleField] : []
    if (titleField) {
      await promptAdditionalSchemaFields(kind, schemasDir, fields)
    } else {
      while (fields.length === 0) {
        await promptAdditionalSchemaFields(kind, schemasDir, fields)
        if (fields.length === 0) {
          p.log.warn('Tabs need at least one child field.')
        }
      }
    }
    return { name, label, fields }
  }

  const slotFields = await promptTabSlotFields(kind, schemasDir, titleField)
  return {
    name,
    label,
    slot: {
      ...(slotFields.main.length > 0 ? { main: { fields: slotFields.main } } : {}),
      ...(slotFields.sidebar.length > 0 ? { sidebar: { fields: slotFields.sidebar } } : {})
    }
  }
}

async function promptSchemaTabsField(
  kind: 'entity' | 'single',
  schemasDir: string,
  titleField: SchemaField
): Promise<SchemaField> {
  const label = await promptText(CREATE_PROMPT_CONTEXT, 'Tabs field label', 'Content')
  const name = deriveAvailableFieldName(label, [titleField.name])
  const tabs: SchemaTab[] = []
  tabs.push(await promptSchemaTab(kind, schemasDir, titleField, tabs))

  while (await promptConfirm(CREATE_PROMPT_CONTEXT, 'Add another tab?', false)) {
    tabs.push(await promptSchemaTab(kind, schemasDir, undefined, tabs))
  }

  return {
    name,
    type: 'tabs',
    label,
    tabs
  }
}

async function createEntityOrSingleSchema(
  kind: 'entity' | 'single',
  metadata: SchemaMetadata,
  schemasDir: string
): Promise<AuthoredEntityOrSingleSchema> {
  const titleField = createSchemaTitleField(metadata.titleLabel)
  const layout = await promptSelectValue<EntitySingleLayout>(
    CREATE_PROMPT_CONTEXT,
    'Schema layout',
    [
      { value: 'fields', label: 'Single fields array' },
      { value: 'slot', label: 'Main/sidebar slots' },
      { value: 'tabs', label: 'Top-level tabs' }
    ]
  )

  const schema: AuthoredEntityOrSingleSchema = {
    name: metadata.name,
    type: kind,
    label: metadata.label,
    description: metadata.description,
    icon: metadata.icon,
    ...(kind === 'entity'
      ? { columns: [{ accessorKey: 'title', header: metadata.titleLabel, type: 'text' as const }] }
      : {})
  }

  if (layout === 'slot') {
    schema.slot = await promptSchemaSlotFields(kind, schemasDir, titleField)
    return schema
  }

  if (layout === 'tabs') {
    schema.fields = [await promptSchemaTabsField(kind, schemasDir, titleField)]
    return schema
  }

  const fields: SchemaField[] = [titleField]
  await promptAdditionalSchemaFields(kind, schemasDir, fields)
  schema.fields = fields
  return schema
}

async function createFormSchema(metadata: SchemaMetadata, schemasDir: string): Promise<FormSchema> {
  const titleField = createFormTitleField(metadata.titleLabel)
  const schema: FormSchema = {
    name: metadata.name,
    type: 'form',
    label: metadata.label,
    description: metadata.description,
    icon: metadata.icon
  }

  const fields: FormField[] = [titleField]
  await promptAdditionalFormFields(schemasDir, fields)
  schema.fields = fields
  return schema
}

function getUserAddedSubmissionColumnFields(
  fields: FormField[]
): Array<FormField & { name: string }> {
  return fields.filter((field): field is FormField & { name: string } => {
    return Boolean(field.name && field.name !== 'title' && formColumnTypeForField(field))
  })
}

async function promptFormSubmissionColumns(schema: FormSchema): Promise<void> {
  const fields = getUserAddedSubmissionColumnFields(schema.fields ?? [])
  if (fields.length === 0) {
    schema.columns = []
    return
  }

  const existingColumnNames = schema.columns
    ? new Set(schema.columns.map((column) => column.accessorKey))
    : undefined
  const initialValues = existingColumnNames
    ? fields.filter((field) => existingColumnNames.has(field.name)).map((field) => field.name)
    : fields.map((field) => field.name)
  const selected = await p.multiselect<string>({
    message: 'Submission table columns',
    options: fields.map((field) => ({
      value: field.name,
      label: displayLabel(field)
    })),
    initialValues,
    required: false
  })

  if (p.isCancel(selected)) {
    cancel(CREATE_PROMPT_CONTEXT)
  }

  const selectedNames = new Set(selected as string[] | undefined)
  schema.columns = fields
    .filter((field) => selectedNames.has(field.name))
    .map((field): FormColumn => {
      const type = formColumnTypeForField(field)
      return {
        accessorKey: field.name,
        header: displayLabel(field),
        ...(type ? { type } : {})
      }
    })
}

async function promptAdditionalCreateField(
  kind: CreateSchemaKind,
  schemasDir: string,
  schema: LoadedAuthoredGeneratedSchema['schema']
): Promise<void> {
  if (kind === 'form') {
    const formSchema = schema as FormSchema
    formSchema.fields = formSchema.fields ?? []
    await promptFormField(schemasDir, formSchema.fields)
    return
  }

  const entityOrSingleSchema = schema as AuthoredEntityOrSingleSchema
  if (entityOrSingleSchema.slot) {
    const mainFields = entityOrSingleSchema.slot.main?.fields ?? []
    const sidebarFields = entityOrSingleSchema.slot.sidebar?.fields ?? []
    await promptSchemaSlotField(kind, schemasDir, mainFields, sidebarFields)
    entityOrSingleSchema.slot = {
      main: { fields: mainFields },
      ...(sidebarFields.length > 0 ? { sidebar: { fields: sidebarFields } } : {})
    }
    return
  }

  const fields = entityOrSingleSchema.fields ?? []
  entityOrSingleSchema.fields = fields
  fields.push(
    (await promptField(
      CREATE_PROMPT_CONTEXT,
      kind,
      schemasDir,
      'schema',
      0,
      createFieldPromptOptions(fields)
    )) as SchemaField
  )
}

async function promptCreateSchemaAction(): Promise<CreateSchemaAction> {
  return promptSelectValue<CreateSchemaAction>(
    CREATE_PROMPT_CONTEXT,
    'Write schema JSON and generate this schema?',
    [
      { value: 'write', label: 'Yes' },
      { value: 'addMore', label: 'Add more' },
      { value: 'cancel', label: 'Cancel' }
    ],
    'write'
  )
}

function schemaFilePath(kind: CreateSchemaKind, schemasDir: string, schemaName: string): string {
  return kind === 'form'
    ? path.join(schemasDir, 'forms', `${schemaName}.json`)
    : path.join(schemasDir, `${schemaName}.json`)
}

function printPreview(loaded: LoadedAuthoredGeneratedSchema, cwd: string): void {
  p.note(
    `schema: ${loaded.name} (${loaded.kind})\npath: ${path.relative(cwd, loaded.filePath)}\nowner: user\n\n${stringifyProjectJson(loaded.schema).trim()}`,
    'Create schema preview'
  )
}

export async function runCreateCommand(
  kindInput: CreateSchemaKind,
  schemaName: string | undefined,
  options: CreateCommandOptions
): Promise<void> {
  const kind = normalizeKind(kindInput)
  const cwd = options.cwd ? path.resolve(options.cwd) : process.cwd()

  if (!snapshotRootExists(cwd)) {
    fail(
      'Project state is unsupported or incomplete. Re-run `betterstart init --force` to create the current snapshot-backed layout.'
    )
  }

  if (!isInteractiveSession()) {
    fail('Interactive create requires a TTY.')
  }

  const config = await resolveConfig(cwd)
  const paths = resolveProjectPaths(config)
  const schemasDir = path.join(cwd, ...paths.schemasDir.split('/'))
  const metadata = await promptMetadata(kind, schemaName, schemasDir)

  const schema =
    kind === 'form'
      ? await createFormSchema(metadata, schemasDir)
      : await createEntityOrSingleSchema(kind, metadata, schemasDir)
  const filePath = schemaFilePath(kind, schemasDir, metadata.name)
  const loaded: LoadedAuthoredGeneratedSchema = {
    kind,
    name: metadata.name,
    filePath,
    schema
  }

  while (true) {
    if (kind === 'form') {
      await promptFormSubmissionColumns(schema as FormSchema)
    }

    const validationErrors = validateAuthoredGeneratedSchema(loaded)
    if (validationErrors.length > 0) {
      fail(
        `Schema validation failed:\n${validationErrors.map((error) => `  - ${error}`).join('\n')}`
      )
    }

    printPreview(loaded, cwd)
    const action = await promptCreateSchemaAction()
    if (action === 'write') {
      break
    }
    if (action === 'cancel') {
      cancel(CREATE_PROMPT_CONTEXT)
    }
    await promptAdditionalCreateField(kind, schemasDir, schema)
  }

  fs.mkdirSync(path.dirname(filePath), { recursive: true })
  writeAuthoredGeneratedSchema(loaded)
  p.log.success(`Created ${path.relative(cwd, filePath)}`)

  try {
    await runGenerateCommand(metadata.name, {
      force: false,
      acceptGenerated: Boolean(options.acceptGenerated),
      all: false,
      interactive: true,
      skipMigration: Boolean(options.skipMigration),
      cwd
    })
  } catch (error) {
    p.log.error(error instanceof Error ? error.message : String(error))
    p.log.info(
      `Schema JSON was kept. Re-run \`betterstart generate ${metadata.name}${options.skipMigration ? ' --skip-migration' : ''}\` after resolving the issue.`
    )
    process.exit(1)
  }
}
